import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiGet, apiPost } from "../utils/api";
import { downloadSampleCsv } from "../utils/csvSamples";
import { socket } from "../socket";
import {
  FaArrowLeft, FaCheckCircle, FaTimesCircle, FaClock, FaSyncAlt,
  FaPaperPlane, FaWhatsapp, FaBullhorn, FaUpload,
} from "react-icons/fa";
import {
  Card, CardBody, CardHeader, Spinner, Badge,
  TableWrapper, Table, Th, Td,
} from "../components/ui";

// Simple CSV → array-of-rows parser. Detects header line, auto-maps common
// column names (phone/firstName/lastName/email/companyName/tags). Tolerates
// quoted values with commas inside.
function parseCsv(text) {
  const lines = String(text || "")
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length === 0) return [];
  const split = (line) => {
    const out = [];
    let cur = "", inQ = false;
    for (let i = 0; i < line.length; i++) {
      const c = line[i];
      if (c === '"' && line[i + 1] === '"') { cur += '"'; i++; continue; }
      if (c === '"') { inQ = !inQ; continue; }
      if (c === "," && !inQ) { out.push(cur); cur = ""; continue; }
      cur += c;
    }
    out.push(cur);
    return out.map((s) => s.trim());
  };
  const header = split(lines[0]).map((h) => h.toLowerCase());
  const idx = (...names) => {
    for (const n of names) {
      const i = header.indexOf(n);
      if (i !== -1) return i;
    }
    return -1;
  };
  const phoneI = idx("phone", "number", "mobile", "whatsapp", "to");
  const fnI = idx("firstname", "first_name", "first name");
  const lnI = idx("lastname", "last_name", "last name");
  const emI = idx("email", "mail");
  const coI = idx("company", "companyname", "company name");
  const tgI = idx("tags");
  if (phoneI === -1) return [];
  // Original (non-lowercased) headers so {{varName}} preserves the exact key.
  const headerOriginal = split(lines[0]);
  // Indexes used by the mapped fields — everything else becomes a custom field.
  const mappedIdx = new Set([phoneI, fnI, lnI, emI, coI, tgI].filter((i) => i >= 0));
  return lines.slice(1).map((line) => {
    const cols = split(line);
    const tagStr = tgI >= 0 ? (cols[tgI] || "") : "";
    const customFields = [];
    for (let i = 0; i < headerOriginal.length; i++) {
      if (mappedIdx.has(i)) continue;
      const key = (headerOriginal[i] || "").trim();
      const value = (cols[i] || "").trim();
      if (key && value) customFields.push({ key, value });
    }
    return {
      phone: cols[phoneI] || "",
      firstName: fnI >= 0 ? cols[fnI] : undefined,
      lastName:  lnI >= 0 ? cols[lnI] : undefined,
      email:     emI >= 0 ? cols[emI] : undefined,
      companyName: coI >= 0 ? cols[coI] : undefined,
      tags: tagStr ? tagStr.split(/[,;|]/).map((t) => t.trim()).filter(Boolean) : undefined,
      customFields: customFields.length > 0 ? customFields : undefined,
    };
  }).filter((r) => r.phone);
}

const TYPE_STYLE = {
  sent:      { icon: FaCheckCircle, label: "Sent",      cls: "text-emerald-600 bg-emerald-500/10" },
  failed:    { icon: FaTimesCircle, label: "Failed",    cls: "text-red-500 bg-red-500/10" },
  retry:     { icon: FaSyncAlt,     label: "Retry",     cls: "text-amber-500 bg-amber-500/10" },
  scheduled: { icon: FaClock,       label: "Scheduled", cls: "text-blue-500 bg-blue-500/10" },
  paused:    { icon: FaClock,       label: "Paused",    cls: "text-yellow-600 bg-yellow-500/10" },
  completed: { icon: FaCheckCircle, label: "Completed", cls: "text-emerald-600 bg-emerald-500/10" },
  skipped:   { icon: FaClock,       label: "Skipped",   cls: "text-ink-faint bg-ink-faint/10" },
};

function fmtTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString();
}

function contactLabel(c) {
  if (!c) return "—";
  const name = [c.firstName, c.lastName].filter(Boolean).join(" ");
  return name || c.phone || c.id || "—";
}

export default function CampaignAnalytics() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [diag, setDiag] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [resuming, setResuming] = useState(false);

  const handleCsvFile = useCallback(async (file) => {
    if (!file) return;
    setUploadResult(null);
    setUploading(true);
    try {
      const text = await file.text();
      const rows = parseCsv(text);
      if (rows.length === 0) {
        setUploadResult({ ok: false, msg: "No usable rows. Make sure your CSV has a 'phone' column." });
        return;
      }
      // Catch Excel's scientific-notation phone export before sending — those
      // digits are already lost and the server can't recover them.
      const sciRows = rows.filter((r) => /[eE][+-]?\d/.test(String(r.phone || "")));
      if (sciRows.length > 0) {
        setUploadResult({
          ok: false,
          msg:
            `${sciRows.length} of ${rows.length} phone numbers were saved in scientific notation by Excel ` +
            `(e.g. ${sciRows[0].phone}). The original digits are lost. ` +
            `Open the source file, select the phone column, set its format to Text, then re-export as CSV.`,
        });
        return;
      }
      const res = await apiPost(`/api/outreach/${campaignId}/assign-csv`, { rows });
      if (res?.success) {
        setUploadResult({
          ok: true,
          enrolled: res.enrolled,
          skipped: res.skipped,
          total: res.total,
          results: res.results || [],
        });
        // Refresh diagnostics + events so the new executions show up
        fetchAll();
      } else {
        setUploadResult({ ok: false, msg: res?.message || "Upload failed" });
      }
    } catch (err) {
      setUploadResult({ ok: false, msg: err?.message || "Upload error" });
    } finally {
      setUploading(false);
    }
  }, [campaignId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAll = useCallback(async () => {
    if (!campaignId) return;
    setLoading(true);
    try {
      const [d, e] = await Promise.all([
        apiGet(`/api/outreach/${campaignId}/diagnostics`),
        apiGet(`/api/outreach/${campaignId}/events?limit=300`),
      ]);
      if (d?.success) setDiag(d.data);
      if (e?.success) setEvents(Array.isArray(e.data) ? e.data : []);
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Live updates via socket
  useEffect(() => {
    const onUpdate = (evt) => {
      if (!evt || evt.campaignId !== campaignId) return;
      // Prepend so the most recent event is at the top
      setEvents((prev) => [
        {
          id: `live-${evt.executionId}-${evt.at}`,
          executionId: evt.executionId,
          type: evt.type,
          messageIndex: evt.messageIndex,
          detail: evt.detail,
          createdAt: evt.at,
          live: true,
        },
        ...prev,
      ].slice(0, 500));
    };
    socket.on("outreach:execution_update", onUpdate);
    return () => socket.off("outreach:execution_update", onUpdate);
  }, [campaignId]);

  // Light auto-refresh every 15s as a backstop in case a socket event is missed
  useEffect(() => {
    const t = setInterval(fetchAll, 15000);
    return () => clearInterval(t);
  }, [fetchAll]);

  if (loading && !diag) {
    return (
      <div className="page-content">
        <div className="flex items-center justify-center py-16"><Spinner size="lg" /></div>
      </div>
    );
  }

  if (!diag) {
    return (
      <div className="page-content">
        <div className="text-center py-16 text-ink-muted">
          Couldn’t load campaign analytics.
        </div>
      </div>
    );
  }

  const counts = diag.executionCounts || { total: 0, running: 0, completed: 0, stopped: 0, replied: 0 };
  const failedExecutions = (diag.executions || []).filter((e) => e.lastError);
  const sessionConn = diag.sessionConnectivity || {};
  const sessionPhones = diag.sessionPhones || {};
  const sessionLabel = (sid) => {
    const phone = sessionPhones[sid];
    return phone ? `+${phone}` : sid;
  };

  // Detect the "old number disconnected, new number connected" recovery case.
  // Eligible: stopped executions whose lastError reads like a session-disconnect
  // and whose sessionId is not currently in campaign.sessionIds (or whose
  // sessionId is connected=false).
  const SESSION_DISCONNECT_RE = /(session not connected|timeout waiting for reply on wa:reply|not connected|session.*expired)/i;
  const currentSessionIds = diag.campaign?.sessionIds || [];
  const stuckExecutions = (diag.executions || []).filter(
    (e) => e.status === "stopped" && SESSION_DISCONNECT_RE.test(e.lastError || "")
  );
  const stuckSessionIds = Array.from(new Set(stuckExecutions.map((e) => e.sessionId).filter(Boolean)));
  const stuckSessionIdsNotInCampaign = stuckSessionIds.filter((sid) => !currentSessionIds.includes(sid));
  const connectedTargetSessionId = currentSessionIds.find((sid) => sessionConn[sid]);
  const canResume = stuckExecutions.length > 0 && !!connectedTargetSessionId;
  const handleResume = async () => {
    if (!connectedTargetSessionId) return;
    if (!window.confirm(`Resume ${stuckExecutions.length} stopped lead${stuckExecutions.length === 1 ? "" : "s"} on ${sessionLabel(connectedTargetSessionId)}? They'll continue from where they left off in the message sequence.`)) return;
    try {
      setResuming(true);
      const res = await apiPost(`/api/outreach/${campaignId}/resume-on-session`, {
        toSessionId: connectedTargetSessionId,
        fromSessionIds: stuckSessionIdsNotInCampaign.length > 0 ? stuckSessionIdsNotInCampaign : stuckSessionIds,
      });
      if (res?.success) {
        // Refetch
        const [d] = await Promise.all([
          apiGet(`/api/outreach/${campaignId}/diagnostics`),
        ]);
        if (d?.success) setDiag(d.data);
      } else {
        alert(res?.message || "Failed to resume.");
      }
    } catch (e) {
      alert(e?.message || "Failed to resume.");
    } finally {
      setResuming(false);
    }
  };

  // Group failure reasons for the analytics card
  const reasonCounts = {};
  for (const ev of events) {
    if (ev.type === "failed" && ev.detail) {
      const key = (ev.detail || "Unknown").slice(0, 80);
      reasonCounts[key] = (reasonCounts[key] || 0) + 1;
    }
  }
  const reasonRows = Object.entries(reasonCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8);

  return (
    <div className="page-content">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button
            className="btn-ghost btn-sm flex items-center gap-1.5"
            onClick={() => navigate(`/campaigns/${campaignId}`)}
          >
            <FaArrowLeft size={11} /> Back
          </button>
          <div className="w-px h-5 bg-surface-border" />
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-brand flex items-center justify-center">
              <FaBullhorn size={12} className="text-white" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-ink-primary">{diag.campaign?.name}</h2>
              <div className="flex items-center gap-2 mt-0.5">
                <Badge variant={diag.campaign?.active ? "success" : "neutral"}>
                  {diag.campaign?.active ? "Active" : "Inactive"}
                </Badge>
                <span className="text-2xs font-mono text-ink-faint">ID: {diag.campaign?.id}</span>
              </div>
            </div>
          </div>
        </div>
        <button className="btn-secondary btn-sm flex items-center gap-1.5" onClick={fetchAll}>
          <FaSyncAlt size={10} /> Refresh
        </button>
      </div>

      {/* CSV upload */}
      <Card className="mb-6">
        <CardBody className="p-5">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-brand/10 text-brand flex items-center justify-center flex-shrink-0">
                <FaUpload size={16} />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-ink-primary">Upload leads via CSV</h3>
                <p className="text-2xs text-ink-muted mt-1 max-w-xl leading-relaxed">
                  Required column: <code className="text-brand">phone</code>.
                  Optional: firstName, lastName, email, companyName, tags. Existing
                  contacts are matched by phone. New contacts are created as leads.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                className="text-xs text-brand hover:text-brand-700 font-medium"
                onClick={() => downloadSampleCsv("contacts")}
              >
                Download sample CSV
              </button>
              <label className="btn-primary btn-sm cursor-pointer flex items-center gap-1.5">
                <FaUpload size={10} />
                {uploading ? "Uploading..." : "Choose file"}
                <input
                  type="file"
                  accept=".csv,.txt"
                  className="hidden"
                  disabled={uploading}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) handleCsvFile(f);
                    e.target.value = "";
                  }}
                />
              </label>
            </div>
          </div>
          {uploadResult && (
            <div
              className={`mt-3 rounded-lg border px-3 py-2 text-xs ${
                uploadResult.ok
                  ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                  : "border-red-200 bg-red-50 text-red-600"
              }`}
            >
              {uploadResult.ok ? (
                <>
                  <strong>Enrolled {uploadResult.enrolled}</strong> of {uploadResult.total} row(s).
                  {uploadResult.skipped > 0 && <> Skipped {uploadResult.skipped}.</>}
                  {uploadResult.results?.some((r) => r.status !== "enrolled") && (
                    <ul className="mt-1.5 list-disc pl-4 text-2xs">
                      {uploadResult.results
                        .filter((r) => r.status !== "enrolled")
                        .slice(0, 8)
                        .map((r, i) => (
                          <li key={i}>
                            <span className="font-mono">{r.phone || "(no phone)"}</span> — {r.status}
                            {r.message ? `: ${r.message}` : ""}
                          </li>
                        ))}
                    </ul>
                  )}
                </>
              ) : (
                uploadResult.msg
              )}
            </div>
          )}
        </CardBody>
      </Card>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <SummaryCard label="Total Leads" value={counts.total} />
        <SummaryCard label="Running" value={counts.running} color="text-blue-500" />
        <SummaryCard label="Completed" value={counts.completed} color="text-emerald-600" />
        <SummaryCard label="Stopped" value={counts.stopped} color="text-amber-500" />
        <SummaryCard label="Replied" value={counts.replied} color="text-indigo-500" />
      </div>

      {/* Resume banner — shown when stopped leads can be migrated to a connected session */}
      {canResume && (
        <div className="mb-6 p-4 rounded-lg border border-amber-300 bg-amber-50 flex items-start justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-amber-900">
              {stuckExecutions.length} lead{stuckExecutions.length === 1 ? "" : "s"} stopped because the original sender disconnected
            </p>
            <p className="text-xs text-amber-800 mt-1">
              Resume them on <span className="font-medium">{sessionLabel(connectedTargetSessionId)}</span> — each lead picks up at the same step in the message sequence (no re-spam from message #1).
              {!diag.campaign?.active && " The campaign will also be reactivated."}
            </p>
          </div>
          <button
            type="button"
            className="btn-primary whitespace-nowrap"
            onClick={handleResume}
            disabled={resuming}
          >
            {resuming ? "Resuming…" : `Resume ${stuckExecutions.length} lead${stuckExecutions.length === 1 ? "" : "s"}`}
          </button>
        </div>
      )}

      {/* Sessions + failure reasons */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <Card>
          <CardHeader className="text-sm font-semibold">Sessions</CardHeader>
          <CardBody className="space-y-2">
            {(diag.campaign?.sessionIds || []).map((sid) => (
              <div key={sid} className="flex items-center justify-between border-b border-surface-border pb-2 last:border-0 last:pb-0">
                <div className="flex items-center gap-2 min-w-0">
                  <FaWhatsapp size={11} className="text-ink-faint flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-ink-primary truncate">
                      {sessionPhones[sid] ? `+${sessionPhones[sid]}` : "Unknown number"}
                    </div>
                    <div className="text-2xs font-mono text-ink-faint truncate" title={sid}>{sid}</div>
                  </div>
                </div>
                <Badge variant={sessionConn[sid] ? "success" : "neutral"}>
                  {sessionConn[sid] ? "Connected" : "Probe says down"}
                </Badge>
              </div>
            ))}
            {(diag.campaign?.sessionIds || []).length === 0 && (
              <p className="text-xs text-ink-muted">No sessions assigned.</p>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader className="text-sm font-semibold">Top failure reasons</CardHeader>
          <CardBody>
            {reasonRows.length === 0 ? (
              <p className="text-xs text-ink-muted">No failures recorded yet.</p>
            ) : (
              <div className="space-y-1.5">
                {reasonRows.map(([reason, count]) => (
                  <div key={reason} className="flex items-center justify-between gap-3">
                    <span className="text-xs text-ink-primary truncate" title={reason}>{reason}</span>
                    <span className="text-2xs font-mono text-red-500 flex-shrink-0">{count}</span>
                  </div>
                ))}
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      {/* Failed executions detail */}
      {failedExecutions.length > 0 && (
        <Card className="mb-6">
          <CardHeader className="text-sm font-semibold flex items-center gap-2">
            <FaTimesCircle className="text-red-500" />
            Failed leads ({failedExecutions.length})
          </CardHeader>
          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <Th>Contact</Th>
                  <Th>Session</Th>
                  <Th>Last error</Th>
                  <Th>When</Th>
                </tr>
              </thead>
              <tbody>
                {failedExecutions.map((e) => (
                  <tr key={e.id}>
                    <Td>
                      <div className="text-xs">
                        <div className="font-medium text-ink-primary">{contactLabel(e.contact)}</div>
                        <div className="text-2xs text-ink-faint font-mono">{e.contact?.phone}</div>
                      </div>
                    </Td>
                    <Td className="text-xs">
                      <div className="font-medium text-ink-primary">{sessionLabel(e.sessionId)}</div>
                      {sessionPhones[e.sessionId] && (
                        <div className="text-2xs font-mono text-ink-faint truncate" title={e.sessionId}>{e.sessionId}</div>
                      )}
                    </Td>
                    <Td className="text-xs text-red-500" title={e.lastError}>
                      {e.lastError?.slice(0, 120) || "—"}
                    </Td>
                    <Td className="text-2xs text-ink-muted">{fmtTime(e.lastErrorAt)}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrapper>
        </Card>
      )}

      {/* Live activity log */}
      <Card>
        <CardHeader className="text-sm font-semibold flex items-center gap-2">
          <FaPaperPlane className="text-brand" />
          Activity log
          <span className="ml-auto text-2xs text-ink-faint">Auto-refresh every 15s · live socket</span>
        </CardHeader>
        <TableWrapper>
          <Table>
            <thead>
              <tr>
                <Th>When</Th>
                <Th>Event</Th>
                <Th>Lead</Th>
                <Th>Message #</Th>
                <Th>Detail</Th>
              </tr>
            </thead>
            <tbody>
              {events.length === 0 ? (
                <tr>
                  <Td colSpan="5" className="text-center text-ink-muted py-8">
                    No events yet. Sends will appear here in real time.
                  </Td>
                </tr>
              ) : events.map((ev) => {
                const t = TYPE_STYLE[ev.type] || TYPE_STYLE.skipped;
                const Icon = t.icon;
                return (
                  <tr key={ev.id} className={ev.live ? "bg-brand/5" : ""}>
                    <Td className="text-2xs text-ink-muted whitespace-nowrap">{fmtTime(ev.createdAt)}</Td>
                    <Td>
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-2xs font-medium ${t.cls}`}>
                        <Icon size={9} /> {t.label}
                      </span>
                    </Td>
                    <Td className="text-xs">
                      <div className="font-medium text-ink-primary">{contactLabel(ev.contact)}</div>
                      <div className="text-2xs text-ink-faint font-mono">{ev.contact?.phone}</div>
                    </Td>
                    <Td className="text-xs">{ev.messageIndex != null ? `#${ev.messageIndex + 1}` : "—"}</Td>
                    <Td className="text-xs text-ink-muted" title={ev.detail}>
                      {(ev.detail || "").slice(0, 160)}
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </TableWrapper>
      </Card>
    </div>
  );
}

function SummaryCard({ label, value, color = "text-ink-primary" }) {
  return (
    <Card>
      <CardBody className="text-center">
        <p className="text-2xs uppercase tracking-wide text-ink-faint">{label}</p>
        <p className={`text-2xl font-bold mt-1 ${color}`}>{value ?? 0}</p>
      </CardBody>
    </Card>
  );
}
